import { motion } from "framer-motion";
import { useState } from "react";
import type { Question } from "../data/questions";
import { cn } from "../lib/utils";

interface QuestionCardProps {
  question: Question;
  initialAnswer?: number;
  onAnswer: (answerIdx: number) => void;
}

export function QuestionCard({ question, initialAnswer, onAnswer }: QuestionCardProps) {
  const [picked, setPicked] = useState<number | null>(
    typeof initialAnswer === "number" ? initialAnswer : null
  );
  const [submitted, setSubmitted] = useState(false);

  const handleClick = (idx: number) => {
    if (submitted) return; // évite double-tap pendant la transition
    setPicked(idx);
    setSubmitted(true);
    window.setTimeout(() => onAnswer(idx), 220);
  };

  return (
    <motion.div
      key={question.id}
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="w-full"
    >
      <div className="text-center mb-6">
        <span className="text-5xl block mb-3" aria-hidden>{question.emoji}</span>
        <h2 className="font-heading font-bold text-2xl text-foreground">
          {question.question}
        </h2>
      </div>

      <div className="space-y-3">
        {question.answers.map((a, idx) => {
          const isPicked = picked === idx;
          return (
            <motion.button
              key={idx}
              type="button"
              onClick={() => handleClick(idx)}
              whileHover={{ scale: submitted ? 1 : 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={cn(
                "w-full text-left rounded-2xl bg-white px-6 py-5 font-body text-foreground transition-all border shadow-card no-tap-highlight",
                isPicked
                  ? "border-primary ring-2 ring-primary/30"
                  : "border-border hover:border-primary"
              )}
              aria-pressed={isPicked}
              disabled={submitted && !isPicked}
            >
              <span className="flex items-center justify-between gap-3">
                <span>{a.text}</span>
                {isPicked && (
                  <span className="shrink-0 h-6 w-6 rounded-full bg-primary text-white grid place-items-center" aria-hidden>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                )}
              </span>
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
}
